import { useCallback, type ComponentType, type SVGProps } from 'react';
import { Bot, Boxes } from 'lucide-react';
// Concrete brand glyphs, imported one file deep instead of through
// `@lobehub/icons`. The barrel re-exports `ProviderIcon`/`AgentIcon`, which
// dispatch over a static map of all 332 brands, so importing either one pulls
// every icon in the library into the bundle — 3.8 MB of web's 7.5 MB entry
// chunk, plus antd and @reduxjs/toolkit, which reach it through the `Avatar`
// variant's `features/IconAvatar`. We render ten of those brands.
//
// `components/Mono` is exactly what `<ProviderIcon type="mono">` resolved to:
// each `es/<Brand>/index.js` sets `Icons = Mono` and hangs `.Text`/`.Avatar`
// off it, and the mono branch renders `Render.Icon` — the same component. The
// brand each id resolved to was read out of lobehub's own `providerConfig` /
// `agentConfig` keyword maps rather than guessed; see BRAND_GLYPHS.
//
// Importing the file rather than the `es/<Brand>` directory also keeps these
// resolvable under plain Node ESM, which does not do directory-index lookup.
import AnthropicGlyph from '@lobehub/icons/es/Anthropic/components/Mono';
import ClaudeGlyph from '@lobehub/icons/es/Claude/components/Mono';
import { ClaudeCodeIcon } from '../assets/icons/ClaudeCodeIcon';
import BasetenGlyph from '@lobehub/icons/es/Baseten/components/Mono';
import CursorGlyph from '@lobehub/icons/es/Cursor/components/Mono';
import FireworksGlyph from '@lobehub/icons/es/Fireworks/components/Mono';
import GoogleGlyph from '@lobehub/icons/es/Google/components/Mono';
import OpenAIGlyph from '@lobehub/icons/es/OpenAI/components/Mono';
import OpenCodeGlyph from '@lobehub/icons/es/OpenCode/components/Mono';
import OpenRouterGlyph from '@lobehub/icons/es/OpenRouter/components/Mono';
import VercelGlyph from '@lobehub/icons/es/Vercel/components/Mono';
import XAIGlyph from '@lobehub/icons/es/XAI/components/Mono';
import {
  matchModelProviderByPrefix,
  MODEL_PROVIDER_LABELS,
  resolveModelDisplayName,
  type ChatModel,
  type ChatProvider,
  type ModelProvider,
} from '@reflex/shared';
import { cn } from './utils';

/**
 * Icon component contract used across the chat/model UI. Callers render the
 * resolved `icon` with a `className` that drives sizing (e.g. `h-4 w-4`), so
 * every concrete icon — lucide glyphs and the lobehub brand wrappers below —
 * must accept SVG props.
 */
type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

export interface ModelIconConfig {
  icon: IconComponent;
  color: string;
  bgColor: string;
  shortName: string;
  /** Brand SVGs already carry their own colors — skip applying `color` as a className. */
  isBrandSvg?: boolean;
}

export interface ModelConfig extends ModelIconConfig {
  id: ChatModel;
  name: string;
  description: string;
  provider: ChatProvider;
  badge?: string;
  badgeClass?: string;
  /** True for superseded releases kept only for historical cost pricing. */
  superseded?: boolean;
}

/**
 * The brand glyph each id resolves to. Keys are the ids this module already
 * used at its call sites, so the mapping is the one lobehub's dispatchers
 * performed at runtime, now made explicit:
 *
 * - `anthropic`/`openai`/`google`/`xai`/`openrouter`/`fireworksai`/`baseten`/
 *   `vercel` matched those keywords in `providerConfig`'s `providerMappings`.
 * - `cursor` matched NOTHING in `providerMappings` (lobehub files it under
 *   `agentConfig` instead), which is why the old code carried a
 *   `PROVIDER_ICON_VIA_AGENT` escape hatch for it. `opencode` is likewise an
 *   agent-only brand. Both resolve to a brand glyph all the same, so the
 *   provider/harness split no longer needs to exist here.
 *
 * Note `fireworksai`: Reflex exposes the provider as `fireworks-ai` and
 * lobehub keys it `fireworksai`, so the call site passes lobehub's spelling.
 *
 * Adding a provider means adding its glyph import above and an entry here —
 * deliberately explicit, because the alternative is shipping all 332 brands.
 * Providers with no lobehub glyph (nebius, runloop) use a lucide fallback at
 * their call site and never appear here.
 */
const BRAND_GLYPHS = {
  anthropic: AnthropicGlyph,
  claude: ClaudeGlyph,
  baseten: BasetenGlyph,
  cursor: CursorGlyph,
  fireworksai: FireworksGlyph,
  google: GoogleGlyph,
  openai: OpenAIGlyph,
  opencode: OpenCodeGlyph,
  openrouter: OpenRouterGlyph,
  vercel: VercelGlyph,
  xai: XAIGlyph,
} satisfies Record<string, IconComponent>;

type BrandId = keyof typeof BRAND_GLYPHS;

/**
 * lobehub's `ProviderIcon`/`AgentIcon` defaulted `size` to 12 and passed it
 * down to the glyph, whose own default is `1em`. Call sites drive the rendered
 * size with a `h-4 w-4`-style class, but the ones that do not would grow from
 * 12px to 16px if this were dropped.
 */
const LOBEHUB_DEFAULT_SIZE = 12;

/**
 * Build a stable brand-glyph component for a provider or harness id. We render
 * the monochrome (`currentColor`) variant so the recognizable brand shape
 * inherits the brand `color` we apply at the call site, staying legible in both
 * light and dark themes. Components are created once per brand (below) so React
 * keeps a stable element identity.
 *
 * The brand icons are purely decorative, so we mark them `aria-hidden` and
 * strip the `<title>` element lobehub injects (the brand name). That keeps the
 * provider name from being announced twice by screen readers and from leaking
 * into text queries when an icon sits next to its label.
 */
function makeBrandIcon(brand: BrandId): IconComponent {
  const BrandGlyph = BRAND_GLYPHS[brand];

  function BrandIcon({ className, style }: SVGProps<SVGSVGElement>) {
    const stripTitle = useCallback((node: HTMLSpanElement | null) => {
      node?.querySelectorAll('title').forEach((title) => title.remove());
    }, []);
    return (
      <span ref={stripTitle} aria-hidden className="contents">
        <BrandGlyph size={LOBEHUB_DEFAULT_SIZE} className={cn(className)} style={style} />
      </span>
    );
  }
  BrandIcon.displayName = `BrandIcon(${brand})`;
  return BrandIcon;
}

const ANTHROPIC_COLOR = 'text-[#D97757]';
const ANTHROPIC_BG = 'bg-[#D97757]/10';
const ANTHROPIC_BADGE = 'bg-[#D97757]/10 text-[#D97757]';

/**
 * Brand-tinted row surfaces for the model picker. Hover / keyboard highlight
 * is a light wash (`/10`); the checked selection is darker (`/25`). cmdk sets
 * `data-selected` on the highlighted row (not the checked one), so the
 * checked highlight uses `/40` and must not match the resting `/25` fill.
 * Literals must be complete class strings.
 */
const ROW_SURFACE: Record<string, { interactive: string; selected: string }> = {
  [ANTHROPIC_BG]: {
    interactive: 'hover:bg-[#D97757]/10 data-[selected=true]:bg-[#D97757]/10',
    selected: 'bg-[#D97757]/25 data-[selected=true]:bg-[#D97757]/40',
  },
  'bg-status-accent-teal/10': {
    interactive: 'hover:bg-status-accent-teal/10 data-[selected=true]:bg-status-accent-teal/10',
    selected: 'bg-status-accent-teal/25 data-[selected=true]:bg-status-accent-teal/40',
  },
  'bg-status-accent-sky/10': {
    interactive: 'hover:bg-status-accent-sky/10 data-[selected=true]:bg-status-accent-sky/10',
    selected: 'bg-status-accent-sky/25 data-[selected=true]:bg-status-accent-sky/40',
  },
  'bg-status-error/10': {
    interactive: 'hover:bg-status-error/10 data-[selected=true]:bg-status-error/10',
    selected: 'bg-status-error/25 data-[selected=true]:bg-status-error/40',
  },
  'bg-status-accent-violet/10': {
    interactive: 'hover:bg-status-accent-violet/10 data-[selected=true]:bg-status-accent-violet/10',
    selected: 'bg-status-accent-violet/25 data-[selected=true]:bg-status-accent-violet/40',
  },
  'bg-muted': {
    interactive: 'hover:bg-muted/80 data-[selected=true]:bg-muted/80',
    selected: 'bg-accent data-[selected=true]:bg-accent/80',
  },
  'bg-[#5019C5]/10': {
    interactive: 'hover:bg-[#5019C5]/10 data-[selected=true]:bg-[#5019C5]/10',
    selected: 'bg-[#5019C5]/25 data-[selected=true]:bg-[#5019C5]/40',
  },
  'bg-[#19E76E]/10': {
    interactive: 'hover:bg-[#19E76E]/10 data-[selected=true]:bg-[#19E76E]/10',
    selected: 'bg-[#19E76E]/25 data-[selected=true]:bg-[#19E76E]/40',
  },
};

/**
 * Hover / highlight / selected background classes for a model picker row,
 * keyed off the catalog `bgColor`. Falls back to the default accent wash.
 */
export function getModelRowSurfaceClass(bgColor?: string | null, selected = false): string {
  const surface = bgColor ? ROW_SURFACE[bgColor] : undefined;
  if (!surface) {
    return cn(
      'hover:bg-accent/50 data-[selected=true]:bg-accent/50',
      selected && 'bg-accent data-[selected=true]:bg-accent/80',
    );
  }
  return cn(surface.interactive, selected && surface.selected);
}

/**
 * Per-provider icon + color config, keyed on the canonical `ModelProvider`
 * ids. Most keys match lobehub's `ProviderIcon` keys 1:1; Fireworks AI is
 * exposed as `fireworks-ai` in Reflex and `fireworksai` in lobehub's keyword map.
 */
const PROVIDER_CONFIGS: Record<ModelProvider, ModelIconConfig> = {
  anthropic: {
    icon: makeBrandIcon('claude'),
    color: ANTHROPIC_COLOR,
    bgColor: ANTHROPIC_BG,
    shortName: 'Claude',
  },
  openai: {
    icon: makeBrandIcon('openai'),
    color: 'text-status-accent-teal',
    bgColor: 'bg-status-accent-teal/10',
    shortName: 'OpenAI',
  },
  google: {
    icon: makeBrandIcon('google'),
    color: 'text-status-accent-sky',
    bgColor: 'bg-status-accent-sky/10',
    shortName: 'Gemini',
  },
  xai: {
    icon: makeBrandIcon('xai'),
    color: 'text-status-error',
    bgColor: 'bg-status-error/10',
    shortName: 'Grok',
  },
  openrouter: {
    icon: makeBrandIcon('openrouter'),
    color: 'text-muted-foreground',
    bgColor: 'bg-muted',
    shortName: 'OpenRouter',
  },
  cursor: {
    icon: makeBrandIcon('cursor'),
    color: 'text-foreground',
    bgColor: 'bg-muted',
    shortName: 'Cursor',
  },
  'fireworks-ai': {
    icon: makeBrandIcon('fireworksai'),
    color: 'text-[#5019C5]',
    bgColor: 'bg-[#5019C5]/10',
    shortName: 'Fireworks',
  },
  baseten: {
    icon: makeBrandIcon('baseten'),
    // Baseten brand green (#19E76E) from https://www.baseten.co/brand/
    color: 'text-[#19E76E]',
    bgColor: 'bg-[#19E76E]/10',
    shortName: 'Baseten',
  },
  vercel: {
    icon: makeBrandIcon('vercel'),
    color: 'text-foreground',
    bgColor: 'bg-muted',
    shortName: 'Vercel',
  },
  nebius: {
    // lobehub ships no Nebius brand glyph, so fall back to a neutral lucide icon.
    icon: Boxes,
    color: 'text-status-accent-sky',
    bgColor: 'bg-status-accent-sky/10',
    shortName: 'Nebius',
  },
  runloop: {
    icon: Boxes,
    color: 'text-status-accent-violet',
    bgColor: 'bg-status-accent-violet/10',
    shortName: 'Runloop',
  },
};

/**
 * Per-harness icon config, keyed on the agent-type ids the agent plugins
 * register (`plugins/plugin-agent-*`). The harness brand identifies who runs
 * the agent, independent of which model provider it is pointed at — an
 * OpenCode agent on a Fireworks model still shows the OpenCode glyph.
 */
const HARNESS_CONFIGS: Record<string, ModelIconConfig> = {
  // Claude Code and Codex intentionally use their vendors' provider logos
  // (Anthropic starburst, OpenAI knot) rather than the lobehub harness glyphs.
  'claude-code': {
    icon: ClaudeCodeIcon,
    color: ANTHROPIC_COLOR,
    bgColor: ANTHROPIC_BG,
    shortName: 'Claude Code',
    isBrandSvg: true,
  },
  codex: {
    icon: makeBrandIcon('openai'),
    color: 'text-status-accent-teal',
    bgColor: 'bg-status-accent-teal/10',
    shortName: 'Codex',
  },
  cursor: {
    icon: makeBrandIcon('cursor'),
    color: 'text-foreground',
    bgColor: 'bg-muted',
    shortName: 'Cursor',
  },
  opencode: {
    icon: makeBrandIcon('opencode'),
    color: 'text-foreground',
    bgColor: 'bg-muted',
    shortName: 'OpenCode',
  },
};

/**
 * Resolve an agent-type id (`claude-code`, `codex`, `opencode`, `cursor`) to
 * its harness brand icon config. Returns `undefined` for unknown harnesses so
 * callers can fall back to a model-derived brand.
 */
export function getAgentHarnessIconConfig(agentType?: string | null): ModelIconConfig | undefined {
  if (!agentType) return undefined;
  const normalized = agentType.toLowerCase();
  if (Object.prototype.hasOwnProperty.call(HARNESS_CONFIGS, normalized)) {
    return HARNESS_CONFIGS[normalized];
  }
  return undefined;
}

/**
 * Every model in `ChatModelSchema`, including superseded releases — this is
 * the resolution catalog behind {@link MODEL_MAP} and the icon/name lookups.
 * Selection surfaces read {@link SELECTABLE_MODELS} instead.
 *
 * Catalog order is user-visible: pickers list models in this order, and
 * ChatWelcome shows only the first three models of the selected provider as
 * quick-pick cards. Reordering a provider's models changes which cards render.
 */
export const ALL_MODELS: ModelConfig[] = [
  // Anthropic
  {
    id: 'sonnet',
    name: 'Claude Sonnet',
    shortName: 'Sonnet',
    description: 'Best balance of speed and intelligence',
    icon: PROVIDER_CONFIGS.anthropic.icon,
    color: ANTHROPIC_COLOR,
    bgColor: ANTHROPIC_BG,
    badge: 'Recommended',
    badgeClass: ANTHROPIC_BADGE,
    provider: 'anthropic',
  },
  {
    id: 'sonnet-4-6',
    name: 'Claude Sonnet 4.6',
    shortName: 'Sonnet',
    description: 'Superseded Sonnet release',
    icon: PROVIDER_CONFIGS.anthropic.icon,
    color: ANTHROPIC_COLOR,
    bgColor: ANTHROPIC_BG,
    provider: 'anthropic',
    superseded: true,
  },
  {
    id: 'opus',
    name: 'Claude Opus',
    shortName: 'Opus',
    description: 'Most capable, for complex tasks',
    icon: PROVIDER_CONFIGS.anthropic.icon,
    color: ANTHROPIC_COLOR,
    bgColor: ANTHROPIC_BG,
    badge: 'Powerful',
    badgeClass: 'bg-status-accent-purple/10 text-status-accent-purple',
    provider: 'anthropic',
  },
  {
    id: 'opus-5',
    name: 'Claude Opus 5',
    shortName: 'Opus',
    description: 'Superseded Opus release',
    icon: PROVIDER_CONFIGS.anthropic.icon,
    color: ANTHROPIC_COLOR,
    bgColor: ANTHROPIC_BG,
    provider: 'anthropic',
    superseded: true,
  },
  {
    id: 'haiku',
    name: 'Claude Haiku',
    shortName: 'Haiku',
    description: 'Fastest, for simple tasks',
    icon: PROVIDER_CONFIGS.anthropic.icon,
    color: ANTHROPIC_COLOR,
    bgColor: ANTHROPIC_BG,
    badge: 'Fast',
    badgeClass: 'bg-status-success/10 text-status-success',
    provider: 'anthropic',
  },
  {
    id: 'fable',
    name: 'Claude Fable',
    shortName: 'Fable',
    description: 'Most advanced, for the most demanding tasks',
    icon: PROVIDER_CONFIGS.anthropic.icon,
    color: ANTHROPIC_COLOR,
    bgColor: ANTHROPIC_BG,
    badge: 'Powerful',
    badgeClass: 'bg-status-accent-purple/10 text-status-accent-purple',
    provider: 'anthropic',
  },
  {
    id: 'fable-5',
    name: 'Claude Fable 5',
    shortName: 'Fable',
    description: 'Superseded Fable release',
    icon: PROVIDER_CONFIGS.anthropic.icon,
    color: ANTHROPIC_COLOR,
    bgColor: ANTHROPIC_BG,
    provider: 'anthropic',
    superseded: true,
  },
  // OpenAI
  {
    id: 'gpt-6-astra',
    name: 'GPT-6 Astra',
    shortName: 'GPT-6 Astra',
    description: 'For complex reasoning and coding',
    icon: PROVIDER_CONFIGS.openai.icon,
    color: PROVIDER_CONFIGS.openai.color,
    bgColor: PROVIDER_CONFIGS.openai.bgColor,
    badge: 'Astra',
    badgeClass: 'bg-status-accent-purple/10 text-status-accent-purple',
    provider: 'openai',
  },
  {
    id: 'gpt-6.1-sol',
    name: 'GPT-6.1 Sol',
    shortName: 'GPT-6.1 Sol',
    description: 'For complex coding and everyday work',
    icon: PROVIDER_CONFIGS.openai.icon,
    color: PROVIDER_CONFIGS.openai.color,
    bgColor: PROVIDER_CONFIGS.openai.bgColor,
    badge: 'Balanced',
    badgeClass: 'bg-status-accent-teal/10 text-status-accent-teal',
    provider: 'openai',
  },
  {
    id: 'gpt-5.6-sol',
    name: 'GPT-5.6 Sol',
    shortName: 'GPT-5.6 Sol',
    description: 'Most capable GPT-5.6 model',
    icon: PROVIDER_CONFIGS.openai.icon,
    color: PROVIDER_CONFIGS.openai.color,
    bgColor: PROVIDER_CONFIGS.openai.bgColor,
    badge: 'Powerful',
    badgeClass: 'bg-status-accent-purple/10 text-status-accent-purple',
    provider: 'openai',
  },
  {
    id: 'gpt-5.6-terra',
    name: 'GPT-5.6 Terra',
    shortName: 'GPT-5.6 Terra',
    description: 'Balanced GPT-5.6 model',
    icon: PROVIDER_CONFIGS.openai.icon,
    color: PROVIDER_CONFIGS.openai.color,
    bgColor: PROVIDER_CONFIGS.openai.bgColor,
    badge: 'Recommended',
    badgeClass: 'bg-status-accent-teal/10 text-status-accent-teal',
    provider: 'openai',
  },
  {
    id: 'gpt-5.6-luna',
    name: 'GPT-5.6 Luna',
    shortName: 'GPT-5.6 Luna',
    description: 'Fast and affordable GPT-5.6 model',
    icon: PROVIDER_CONFIGS.openai.icon,
    color: PROVIDER_CONFIGS.openai.color,
    bgColor: PROVIDER_CONFIGS.openai.bgColor,
    badge: 'Fast',
    badgeClass: 'bg-status-success/10 text-status-success',
    provider: 'openai',
  },
  {
    id: 'gpt-5.5',
    name: 'GPT-5.5',
    shortName: 'GPT-5.5',
    description: 'Flagship model',
    icon: PROVIDER_CONFIGS.openai.icon,
    color: PROVIDER_CONFIGS.openai.color,
    bgColor: PROVIDER_CONFIGS.openai.bgColor,
    badge: 'Popular',
    badgeClass: 'bg-status-accent-teal/10 text-status-accent-teal',
    provider: 'openai',
  },
  {
    id: 'gpt-5.4-mini',
    name: 'GPT-5.4 Mini',
    shortName: '5.4 Mini',
    description: 'Fast and affordable',
    icon: PROVIDER_CONFIGS.openai.icon,
    color: PROVIDER_CONFIGS.openai.color,
    bgColor: PROVIDER_CONFIGS.openai.bgColor,
    badge: 'Fast',
    badgeClass: 'bg-status-success/10 text-status-success',
    provider: 'openai',
  },
  {
    id: 'gpt-5.5-pro',
    name: 'GPT-5.5 Pro',
    shortName: '5.5 Pro',
    description: 'Highest quality, slower',
    icon: PROVIDER_CONFIGS.openai.icon,
    color: PROVIDER_CONFIGS.openai.color,
    bgColor: PROVIDER_CONFIGS.openai.bgColor,
    badge: 'Powerful',
    badgeClass: 'bg-status-accent-purple/10 text-status-accent-purple',
    provider: 'openai',
  },
  // Google
  {
    id: 'gemini-2.5-pro',
    name: 'Gemini 2.5 Pro',
    shortName: 'Gemini Pro',
    description: 'Most capable',
    icon: PROVIDER_CONFIGS.google.icon,
    color: PROVIDER_CONFIGS.google.color,
    bgColor: PROVIDER_CONFIGS.google.bgColor,
    provider: 'google',
  },
  {
    id: 'gemini-2.5-flash',
    name: 'Gemini 2.5 Flash',
    shortName: 'Gemini Flash',
    description: 'Fast with large context',
    icon: PROVIDER_CONFIGS.google.icon,
    color: PROVIDER_CONFIGS.google.color,
    bgColor: PROVIDER_CONFIGS.google.bgColor,
    badge: 'Fast',
    badgeClass: 'bg-status-accent-sky/10 text-status-accent-sky',
    provider: 'google',
  },
  // xAI
  {
    id: 'grok-4.20',
    name: 'Grok 4.20',
    shortName: 'Grok 4.20',
    description: 'Flagship model',
    icon: PROVIDER_CONFIGS.xai.icon,
    color: PROVIDER_CONFIGS.xai.color,
    bgColor: PROVIDER_CONFIGS.xai.bgColor,
    provider: 'xai',
  },
  {
    id: 'grok-4.1-fast',
    name: 'Grok 4.1 Fast',
    shortName: 'Grok Fast',
    description: 'Fast and affordable',
    icon: PROVIDER_CONFIGS.xai.icon,
    color: PROVIDER_CONFIGS.xai.color,
    bgColor: PROVIDER_CONFIGS.xai.bgColor,
    provider: 'xai',
  },
  // Nebius
  {
    id: 'glm-5.2',
    name: 'GLM-5.2 (Nebius)',
    shortName: 'GLM-5.2 (Nebius)',
    description: 'Open model on a dedicated Nebius endpoint',
    icon: PROVIDER_CONFIGS.nebius.icon,
    color: PROVIDER_CONFIGS.nebius.color,
    bgColor: PROVIDER_CONFIGS.nebius.bgColor,
    provider: 'nebius',
  },
  {
    id: 'glm-5.2-runloop',
    name: 'GLM-5.2',
    shortName: 'GLM-5.2',
    description: 'Runloop-managed model',
    icon: PROVIDER_CONFIGS.runloop.icon,
    color: PROVIDER_CONFIGS.runloop.color,
    bgColor: PROVIDER_CONFIGS.runloop.bgColor,
    provider: 'runloop',
  },
];

export const MODEL_MAP = new Map(ALL_MODELS.map((m) => [m.id, m]));

/**
 * The models a user may pick: the catalog minus superseded releases. A
 * superseded release stays in `ALL_MODELS` because the catalog mirrors
 * `ChatModelSchema` and something already pinned to that id still needs a name
 * and an icon — but it must never be offered, because it sits beside its
 * successor under the same short name, so a picker listing both shows the same
 * label twice with no way to tell them apart. Every selection surface
 * (pickers, quick-pick cards, fall-off-a-disabled-model defaults) reads this.
 */
export const SELECTABLE_MODELS: ModelConfig[] = ALL_MODELS.filter((m) => !m.superseded);

/**
 * Provider display labels, widened to `Record<string, string>` because callers
 * index it with an arbitrary provider id off a model config. Aliases the shared
 * table rather than restating it, so a provider rename or addition reaches every
 * picker without a second edit (same idiom as `ProviderChip`).
 */
export const PROVIDER_LABELS: Record<string, string> = MODEL_PROVIDER_LABELS;

const DEFAULT_CONFIG: ModelIconConfig = {
  icon: Bot,
  color: 'text-muted-foreground',
  bgColor: 'bg-muted',
  shortName: 'Agent',
};

/**
 * Map an arbitrary model id (e.g. `claude-sonnet-4-20250514`, `gpt-5.5`,
 * `gemini-2.5-pro`, `grok-4`) to its canonical provider id. Returns `undefined`
 * when nothing matches so the caller can fall back to the generic agent icon.
 */
function deriveProvider(model: string): ModelProvider | undefined {
  return matchModelProviderByPrefix(model);
}

function getConfiguredProvider(providerId: string): ModelIconConfig | undefined {
  const normalized = providerId.toLowerCase();
  if (Object.prototype.hasOwnProperty.call(PROVIDER_CONFIGS, normalized)) {
    return PROVIDER_CONFIGS[normalized as ModelProvider];
  }
  return undefined;
}

/**
 * Resolve a model string (e.g. `sonnet`, `claude-sonnet-4-20250514`) to its
 * icon config. Tries the exact chat-model map first, then derives the provider
 * from the id, and finally falls back to the generic agent icon.
 */
export function getModelIconConfig(model?: string | null): ModelIconConfig {
  if (!model) return DEFAULT_CONFIG;

  const exact = MODEL_MAP.get(model as ChatModel);
  if (exact) return exact;

  // Match a known chat model family by substring (e.g. the API id
  // `claude-sonnet-4-20250514` → the `sonnet` config) so model-specific
  // names/badges survive even when the caller passes a full provider model id.
  for (const m of ALL_MODELS) {
    if (model.includes(m.id)) return m;
  }

  const provider = deriveProvider(model);
  if (provider) return getConfiguredProvider(provider) ?? DEFAULT_CONFIG;

  return DEFAULT_CONFIG;
}

/**
 * Human-readable label for a model id. Known chat models resolve to their
 * short name (`sonnet` → `Sonnet`, including full API ids via substring
 * match); anything unrecognized returns the raw model id verbatim rather
 * than a provider-derived name, so users see exactly what they launched.
 *
 * Ids whose raw form is unreadable resolve through
 * {@link resolveModelDisplayName} first. The substring pass below is
 * case-sensitive, so it cannot recover them on its own: the registry id is
 * `glm-5.2` while the Nebius dedicated deployment spells it `GLM-5.2`.
 */
export function getModelDisplayName(model?: string | null): string {
  if (!model) return DEFAULT_CONFIG.shortName;

  const resolved = resolveModelDisplayName(model);

  const exact = MODEL_MAP.get(resolved as ChatModel);
  if (exact) return exact.shortName;

  for (const m of ALL_MODELS) {
    if (resolved.includes(m.id)) return m.shortName;
  }

  return resolved;
}

/**
 * Resolve a canonical provider id to its brand icon config. Falls back to the
 * generic agent icon for unknown providers.
 */
export function getProviderIconConfig(providerId?: string | null): ModelIconConfig {
  if (!providerId) return DEFAULT_CONFIG;
  return getConfiguredProvider(providerId) ?? DEFAULT_CONFIG;
}
